import { useEffect, useState, useCallback } from "react";
import { Share2, Eye, Copy, Check, XCircle, Trash2, Clock, Building2, Mail, MoreVertical, ShieldCheck, Sparkles, RefreshCw, Users } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { ResendShareModal } from "./ResendShareModal";
import { SimilarCoachesModal } from "./SimilarCoachesModal";

const API = process.env.REACT_APP_BACKEND_URL;

export function ShareActivityPanel({ athleteId, onShareClick }) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | active | revoked
  const [resendTarget, setResendTarget] = useState(null); // { share, suggestedNote }
  const [similarSeed, setSimilarSeed] = useState(null); // share object

  const load = useCallback(async () => {
    if (!athleteId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API}/api/players/${athleteId}/shares`, {
        headers: { "Authorization": `Bearer ${token}` },
      });
      const txt = await r.text();
      let data = {};
      try { data = JSON.parse(txt); } catch {}
      if (r.ok) setShares(data.shares || []);
      else toast.error(data.detail || "Failed to load share activity");
    } catch (e) {
      toast.error(e.message || "Network error");
    } finally {
      setLoading(false);
    }
  }, [athleteId]);

  useEffect(() => { load(); }, [load]);

  const visible = shares.filter((s) => {
    if (filter === "active") return !s.revoked;
    if (filter === "revoked") return !!s.revoked;
    return true;
  });

  // Nudges: active, never opened, and last touched > 7 days ago
  const NUDGE_THRESHOLD_DAYS = 7;
  const daysSince = (iso) => {
    if (!iso) return Infinity;
    return (Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24);
  };
  const staleShares = shares.filter((s) => {
    if (s.revoked) return false;
    if ((s.view_count || 0) > 0) return false;
    const lastTouch = s.last_resent_at || s.created_at;
    return daysSince(lastTouch) >= NUDGE_THRESHOLD_DAYS;
  });

  const suggestNudgeNote = (s) => {
    const first = (s.coach_name || "").trim().split(/\s+/).pop() || "Coach";
    return `Hi Coach ${first} — just a quick follow-up. I wanted to make sure you got the report I sent. Would love your evaluation when you have a moment.`;
  };

  const stats = {
    total: shares.length,
    active: shares.filter((s) => !s.revoked).length,
    totalViews: shares.reduce((acc, s) => acc + (s.view_count || 0), 0),
    viewedShares: shares.filter((s) => (s.view_count || 0) > 0).length,
  };

  const handleRevoke = async (share_id) => {
    if (!confirm("Revoke this link? The coach will no longer be able to access your scouting report.")) return;
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API}/api/players/${athleteId}/shares/${share_id}/revoke`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
      });
      const txt = await r.text();
      let data = {};
      try { data = JSON.parse(txt); } catch {}
      if (!r.ok) throw new Error(data.detail || "Revoke failed");
      toast.success("Link revoked");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="share-activity-panel">
      {/* Stats Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Share2} label="Total Links" value={stats.total} color="text-gray-900" />
        <StatCard icon={ShieldCheck} label="Active" value={stats.active} color="text-green-700" />
        <StatCard icon={Eye} label="Total Opens" value={stats.totalViews} color="text-blue-700" />
        <StatCard icon={Sparkles} label="Opened Links" value={`${stats.viewedShares}/${stats.total || 0}`} color="text-amber-700" />
      </div>

      {/* Nudges — stale shares (active, never opened, sat > 7 days) */}
      {staleShares.length > 0 && (
        <div className="rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 p-5" data-testid="nudges-banner">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <Sparkles size={14} className="text-white" />
            </div>
            <div>
              <h3 className="text-sm font-black text-amber-900 tracking-tight">Time to Follow Up</h3>
              <p className="text-[11px] text-amber-800">
                {staleShares.length} {staleShares.length === 1 ? "coach hasn't" : "coaches haven't"} opened your report yet. A nudge often helps.
              </p>
            </div>
          </div>
          <div className="space-y-2">
            {staleShares.slice(0, 5).map((s) => {
              const lastTouch = s.last_resent_at || s.created_at;
              const days = Math.floor(daysSince(lastTouch));
              return (
                <div key={s.share_id} className="bg-white rounded-lg border border-amber-200 p-3 flex items-center gap-3" data-testid={`nudge-row-${s.share_id}`}>
                  <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                    <Clock size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-gray-900 truncate">
                      {s.coach_name || s.coach_email}
                      {s.coach_school && <span className="text-xs font-normal text-gray-500 ml-1.5">· {s.coach_school}</span>}
                    </div>
                    <div className="text-[11px] text-amber-700 font-semibold">Waiting {days} days · still unopened</div>
                  </div>
                  <Button
                    size="sm"
                    className="bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 shadow-sm"
                    onClick={() => setResendTarget({ share: s, suggestedNote: suggestNudgeNote(s) })}
                    data-testid={`nudge-send-${s.share_id}`}
                  >
                    <RefreshCw size={12} className="mr-1.5" /> Send Follow-up
                  </Button>
                </div>
              );
            })}
            {staleShares.length > 5 && (
              <p className="text-[11px] text-amber-700 text-center">+ {staleShares.length - 5} more below</p>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Share Activity</h2>
          <p className="text-sm text-gray-500 mt-0.5">Every private link you've sent to a coach. See who opened your report.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex bg-gray-100 rounded-lg p-1">
            {["all", "active", "revoked"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition ${
                  filter === f ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-700"
                }`}
                data-testid={`share-filter-${f}`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          <Button
            size="sm"
            className="bg-gradient-to-r from-blue-900 to-blue-700 text-white"
            onClick={onShareClick}
            data-testid="share-activity-new-btn"
          >
            <Share2 size={14} className="mr-1.5" /> New Share
          </Button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center text-sm text-gray-400">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-xl p-12 text-center" data-testid="share-empty-state">
          <Share2 size={28} className="text-gray-300 mx-auto mb-3" />
          <h3 className="text-base font-bold text-gray-900">No shares yet</h3>
          <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
            Send your verified scouting report straight to college coaches. They open it without signing in — and you'll see exactly when they did.
          </p>
          <Button className="mt-5 bg-gradient-to-r from-blue-900 to-blue-700 text-white" onClick={onShareClick} data-testid="share-empty-cta">
            <Share2 size={14} className="mr-1.5" /> Share with a Coach
          </Button>
        </div>
      ) : (
        <div className="space-y-2.5">
          {visible.map((s) => (
            <ShareRow
              key={s.share_id}
              share={s}
              onRevoke={handleRevoke}
              onResend={(sh) => setResendTarget({ share: sh })}
              onFindSimilar={(sh) => setSimilarSeed(sh)}
            />
          ))}
        </div>
      )}

      <ResendShareModal
        open={!!resendTarget}
        share={resendTarget?.share}
        suggestedNote={resendTarget?.suggestedNote}
        athleteId={athleteId}
        onClose={() => setResendTarget(null)}
        onResent={load}
      />

      <SimilarCoachesModal
        open={!!similarSeed}
        seedShare={similarSeed}
        athleteId={athleteId}
        onClose={() => setSimilarSeed(null)}
        onShared={load}
      />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-center gap-2 text-gray-400 mb-1">
        <Icon size={12} />
        <span className="text-[10px] uppercase tracking-wider font-bold">{label}</span>
      </div>
      <div className={`text-2xl font-black ${color}`}>{value}</div>
    </div>
  );
}

function ShareRow({ share, onRevoke, onResend, onFindSimilar }) {
  const [showMenu, setShowMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  const isViewed = (share.view_count || 0) > 0;
  const isRevoked = !!share.revoked;

  // Reconstruct share URL using token if it's still on the document (older shares may not have it)
  const shareUrl = share.share_url
    || (share.token ? `${(process.env.REACT_APP_PUBLIC_BASE_URL || "").replace(/\/$/, "") || "https://nationalscoutingbureau.com"}/share/${share.token}` : null);

  const copyLink = async () => {
    if (!shareUrl) {
      toast.error("Link unavailable — please re-issue this share");
      return;
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div
      className={`bg-white border rounded-xl p-4 transition ${isRevoked ? "border-gray-200 opacity-60" : isViewed ? "border-green-200 bg-green-50/30" : "border-gray-200"}`}
      data-testid={`share-row-${share.share_id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isRevoked ? "bg-gray-200 text-gray-400" : isViewed ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
          {isRevoked ? <XCircle size={18} /> : isViewed ? <Eye size={18} /> : <Mail size={18} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-gray-900 truncate" data-testid="share-row-coach-name">
              {share.coach_name || share.coach_email}
            </span>
            {share.coach_school && (
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <Building2 size={11} /> {share.coach_school}
              </span>
            )}
            {isRevoked ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-bold uppercase">
                <XCircle size={9} /> Revoked
              </span>
            ) : isViewed ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-bold uppercase">
                <Eye size={9} /> Viewed {share.view_count}×
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase">
                <Clock size={9} /> Not Yet Opened
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-0.5 truncate" data-testid="share-row-coach-email">{share.coach_email}</div>
          {share.personal_note && (
            <div className="text-xs italic text-gray-600 mt-1.5 border-l-2 border-gray-200 pl-2 max-w-2xl">"{share.personal_note}"</div>
          )}
          <div className="flex items-center gap-3 text-[11px] text-gray-400 mt-2 flex-wrap">
            <span>Sent {timeAgo(share.created_at)}</span>
            {share.last_viewed_at && (
              <span className="text-green-700">· Last opened {timeAgo(share.last_viewed_at)}</span>
            )}
            {share.last_resent_at && (
              <span className="text-blue-600">· Resent {timeAgo(share.last_resent_at)}{share.resend_count > 1 ? ` (${share.resend_count}×)` : ""}</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1.5 relative">
          {!isRevoked && (
            <Button variant="outline" size="sm" onClick={copyLink} className="text-xs" data-testid={`share-row-copy-${share.share_id}`}>
              {copied ? <Check size={12} /> : <Copy size={12} />}
            </Button>
          )}
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700"
            data-testid={`share-row-menu-${share.share_id}`}
          >
            <MoreVertical size={14} />
          </button>
          {showMenu && (
            <div className="absolute right-0 top-9 z-10 w-52 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
              {!isRevoked && (
                <button
                  onClick={() => { setShowMenu(false); onResend && onResend(share); }}
                  className="w-full text-left px-3 py-2 text-xs text-blue-700 hover:bg-blue-50 flex items-center gap-2 border-b border-gray-100"
                  data-testid={`share-row-resend-${share.share_id}`}
                >
                  <RefreshCw size={11} /> Resend email
                </button>
              )}
              <button
                onClick={() => { setShowMenu(false); onFindSimilar && onFindSimilar(share); }}
                className="w-full text-left px-3 py-2 text-xs text-purple-700 hover:bg-purple-50 flex items-center gap-2 border-b border-gray-100"
                data-testid={`share-row-find-similar-${share.share_id}`}
              >
                <Sparkles size={11} /> Find more coaches like this
              </button>
              {!isRevoked && (
                <button
                  onClick={() => { setShowMenu(false); onRevoke(share.share_id); }}
                  className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2"
                  data-testid={`share-row-revoke-${share.share_id}`}
                >
                  <Trash2 size={11} /> Revoke link
                </button>
              )}
              {isRevoked && (
                <div className="px-3 py-2 text-xs text-gray-400 italic">Already revoked</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  const now = new Date();
  const sec = Math.max(0, (now - date) / 1000);
  if (sec < 60) return "just now";
  const min = sec / 60;
  if (min < 60) return `${Math.floor(min)}m ago`;
  const hr = min / 60;
  if (hr < 24) return `${Math.floor(hr)}h ago`;
  const day = hr / 24;
  if (day < 30) return `${Math.floor(day)}d ago`;
  return date.toLocaleDateString();
}
